import React, { Suspense, useEffect, useState } from "react";
import Bugsnag from "@bugsnag/js";
import BugsnagPluginReact from "@bugsnag/plugin-react";

import ErrorBoundary from "../components/error-boundary";

import "../styles/default.scss";
import "../styles/Stepper.css";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import AppProvider from "../components/app-provider";
import { useRouter } from "next/router";
import { Loader } from "../design";
import { CustomerPortalContext } from "../backend/serverProps";
import useNavigationURL from "../hooks/useNavigationURL";

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
        },
    },
});

function initializeBugsnag() {
    if (!(Bugsnag as any)._client) {
        Bugsnag.start({
            apiKey: process.env.NEXT_PUBLIC_BUGSNAG_API_KEY,
            plugins: [new BugsnagPluginReact()],
            autoDetectErrors: false,
        });
    }
}

function App({ Component, pageProps }) {
    initializeBugsnag();
    const router = useRouter();
    const [shopifyDomain, setShopifyDomain] = useState(pageProps?.shopifyDomain ?? process.env.NEXT_PUBLIC_DEV_MYSHOPIFY_DOMAIN);
    const [locale, setLocale] = useState("en");
    const [sessionToken, setSessionToken] = useState(null);

    useEffect(() => {
        if (pageProps?.redirect?.success === false && router.isReady && typeof window !== 'undefined') {
            router.replace(`${pageProps?.redirect?.destination}`);
        }
    }, [router.isReady, pageProps?.redirect?.destination]);

    useEffect(() => {
        if (router.isReady && typeof window !== 'undefined') {
            setSessionToken(router?.query?.sessionToken);
            if (router?.query?.locale) {
                setLocale(router?.query?.locale as string);
            }
            if (process.env.NEXT_PUBLIC_ENV === "production" || process.env.NEXT_PUBLIC_ENV === "staging") {
                setShopifyDomain((window as any).Shopify.shop);
                if ((window as any).Shopify.locale) {
                    setLocale((window as any).Shopify.locale);
                }
            }
        }
    }, [router.isReady]);

    if (!shopifyDomain && !locale) {
        return <Loader />;
    }

    return (
        <ErrorBoundary>
            <QueryClientProvider client={queryClient}>
                <AppProvider
                    shopifyDomain={shopifyDomain}
                    locale={locale}
                    sessionToken={sessionToken}
                    previewThemeId={pageProps?.themeNanoId ?? null}
                    customerPortalContext={pageProps as CustomerPortalContext}
                >
                    <Component {...pageProps} />
                </AppProvider>
            </QueryClientProvider>
        </ErrorBoundary>
    );
}

export default App;
